import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import ChatbotWidget from './ChatbotWidget';
import RicercaGlobale from './RicercaGlobale';
import UpgradeModal from './UpgradeModal';
import { Badge } from './ui/badge';
import { Lock, Search, Landmark } from 'lucide-react';
import {
  LayoutDashboard,
  Users,
  Calendar,
  CalendarClock,
  ClipboardCheck,
  Shield,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  BookOpen,
  UserCircle,
  History,
  Zap,
  Clock,
  Bell,
  Award,
  BarChart3,
  CreditCard,
  UserCheck,
  FolderOpen,
  Heart,
  Trophy,
  ChevronDown,
  ChevronRight,
  Eye,
  UsersRound,
  CalendarDays,
  FolderClosed,
  PieChart,
  Cog,
  ShieldCheck,
  Send,
  Euro,
  Scale,
  FileUp,
  TrendingUp,
  Download,
  Database,
  Gavel,
  Vote,
  Package,
  ArrowLeftRight,
  Car,
  Bot
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { canAccessFeature, getMenuRequiredPlan, getPlanDisplayName, PLAN_HIERARCHY } from '../config/planFeatures';

// Menu structure with categories and plan restrictions based on Matrice v4
// Plans: free < premium < governance < pro < enterprise < convenzioni
const getMenuStructure = (userPlan) => {
  const checkAccess = (path) => {
    const requiredPlan = getMenuRequiredPlan(path);
    return canAccessFeature(userPlan, requiredPlan);
  };

  const getRequiredPlanForPath = (path) => {
    return getMenuRequiredPlan(path);
  };

  return [
    {
      category: 'PANORAMICA',
      icon: Eye,
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/notifiche', label: 'Notifiche', icon: Bell },
        { path: '/comunicazioni', label: 'Comunicazioni', icon: Send, requiredPlan: getRequiredPlanForPath('/comunicazioni'), locked: !checkAccess('/comunicazioni') },
      ]
    },
    {
      category: 'VOLONTARI',
      icon: UsersRound,
      items: [
        { path: '/volontari', label: 'Tutti i Volontari', icon: Users },
        { path: '/volontari-occasionali', label: 'Volontari Occasionali', icon: UserCheck },
        { path: '/volontari-non-occasionali', label: 'Volontari Non Occasionali', icon: UserCheck },
        { path: '/ore-volontari', label: 'Ore Volontari', icon: Clock, requiredPlan: getRequiredPlanForPath('/ore-volontari'), locked: !checkAccess('/ore-volontari') },
        { path: '/presenze', label: 'Presenze', icon: ClipboardCheck },
        { path: '/checkin', label: 'Check-in Rapido', icon: Zap },
        { path: '/statistiche', label: 'Classifiche', icon: Trophy },
        { path: '/diario', label: 'Diario Attività', icon: Award },
        { path: '/kit-amministrativo', label: 'Kit Amministrativo ETS', icon: FolderOpen },
      ]
    },
    {
      category: 'SOCI E ASSOCIATI',
      icon: Users,
      items: [
        { path: '/soci', label: 'Tutti i Soci', icon: Users },
        { path: '/familiari-assistiti', label: 'Familiari e Assistiti', icon: Heart },
        { path: '/registro-soci', label: 'Registro Soci', icon: BookOpen, requiredPlan: getRequiredPlanForPath('/registro-soci'), locked: !checkAccess('/registro-soci') },
        { path: '/quote-associative', label: 'Quote Associative', icon: CreditCard, requiredPlan: getRequiredPlanForPath('/quote-associative'), locked: !checkAccess('/quote-associative') },
        { path: '/kit-soci', label: 'Kit Amministrativo Soci', icon: FolderOpen, requiredPlan: getRequiredPlanForPath('/kit-soci'), locked: !checkAccess('/kit-soci') },
      ]
    },
    {
      category: 'GOVERNANCE',
      icon: Landmark,
      items: [
        { path: '/governance', label: 'Organi Sociali', icon: Landmark, requiredPlan: 'governance', locked: !checkAccess('/governance') },
        { path: '/assemblee', label: 'Assemblee', icon: Vote, requiredPlan: 'governance', locked: !checkAccess('/assemblee') },
        { path: '/consiglio-direttivo', label: 'Consiglio Direttivo', icon: Gavel, requiredPlan: 'governance', locked: !checkAccess('/consiglio-direttivo') },
      ]
    },
    {
      category: 'MAGAZZINO',
      icon: Package,
      items: [
        { path: '/magazzino', label: 'Inventario', icon: Package, requiredPlan: 'pro', locked: !checkAccess('/magazzino') },
        { path: '/magazzino/assegnazioni', label: 'Assegnazioni', icon: ArrowLeftRight, requiredPlan: 'pro', locked: !checkAccess('/magazzino/assegnazioni') },
        { path: '/automezzi', label: 'Automezzi', icon: Car, requiredPlan: 'pro', locked: !checkAccess('/automezzi') },
      ]
    },
    {
      category: 'ATTIVITÀ',
      icon: CalendarDays,
      items: [
        { path: '/eventi', label: 'Eventi', icon: Calendar },
        { path: '/scadenzario', label: 'Calendario Scadenze', icon: CalendarClock, requiredPlan: 'premium', locked: !checkAccess('/scadenzario') },
        { path: '/registro', label: 'Registro', icon: BookOpen, requiredPlan: getRequiredPlanForPath('/registro'), locked: !checkAccess('/registro') },
        { path: '/coperture', label: 'Coperture', icon: Shield },
      ]
    },
    {
      category: 'DOCUMENTI',
      icon: FolderClosed,
      items: [
        { path: '/documenti', label: 'Documenti', icon: FileText },
        { path: '/logs', label: 'Log Operazioni', icon: History, requiredPlan: getRequiredPlanForPath('/logs'), locked: !checkAccess('/logs') },
      ]
    },
    {
      category: 'REPORT E CONTABILITÀ',
      icon: PieChart,
      items: [
        { path: '/reports', label: 'Report', icon: BarChart3 },
        { path: '/rendicontazione', label: 'Rendicontazione Sociale', icon: Heart },
        { path: '/contabilita', label: 'Contabilità Economica', icon: Euro, requiredPlan: 'governance', locked: !checkAccess('/contabilita') },
        { path: '/libri-sociali', label: 'Libri Sociali (PDF)', icon: BookOpen, requiredPlan: 'governance', locked: !checkAccess('/libri-sociali') },
        { path: '/export-runts', label: 'Export', icon: FileUp, requiredPlan: 'premium', locked: !checkAccess('/export-runts') },
        { path: '/bilanci-ministeriali', label: 'Schemi Ministeriali', icon: FileText, requiredPlan: 'governance', locked: !checkAccess('/bilanci-ministeriali') },
        { path: '/statistiche-avanzate', label: 'Statistiche Avanzate', icon: TrendingUp, requiredPlan: 'pro', locked: !checkAccess('/statistiche-avanzate') },
      ]
    },
    {
      category: 'STRUMENTI AVANZATI',
      icon: Award,
      items: [
        { path: '/certificati', label: 'Certificati', icon: Award, requiredPlan: getRequiredPlanForPath('/certificati'), locked: !checkAccess('/certificati') },
        { path: '/import', label: 'Import Massivo', icon: FileUp, requiredPlan: 'governance', locked: !checkAccess('/import') },
      ]
    },
    {
      category: 'IMPOSTAZIONI',
      icon: Cog,
      items: [
        { path: '/impostazioni', label: 'Impostazioni Ente', icon: Settings },
        { path: '/pricing', label: 'Piani e Prezzi', icon: CreditCard },
        { path: '/profilo', label: 'Profilo Utente', icon: UserCircle },
        { path: '/export-dati', label: 'Esporta i Tuoi Dati', icon: Download, requiredPlan: getRequiredPlanForPath('/export-dati'), locked: !checkAccess('/export-dati') },
        { path: '/migrazione-volontari', label: 'Migrazione Dati', icon: Database },
        { path: '/manuale', label: 'Manuale d\'Uso', icon: BookOpen },
        { path: '/termini', label: 'Termini di Utilizzo', icon: Scale },
        { path: '/condizioni-abbonamento', label: 'Condizioni Abbonamento', icon: CreditCard },
        { path: '/privacy', label: 'Privacy Policy', icon: Shield },
        { path: '/cookie-policy', label: 'Cookie Policy', icon: FileText },
        { path: '/uso-intelligenza-artificiale', label: 'Uso dell\'AI', icon: Bot },
      ]
    }
  ];
};

// Admin menu (only for platform_admin)
const adminMenu = {
  category: 'AMMINISTRAZIONE',
  icon: ShieldCheck,
  items: [
    { path: '/admin', label: 'Dashboard Admin', icon: ShieldCheck },
  ],
  adminOnly: true
};

// Category component with expand/collapse
function MenuCategory({ category, icon: CategoryIcon, items, isOpen, onToggle, onItemClick, onLockedItemClick, currentPath, categoryIndex }) {
  const hasActiveItem = items.some(item => currentPath === item.path && !item.locked);
  
  // Colori per la barra laterale delle categorie (ciclici)
  const categoryColors = [
    'bg-blue-500',      // PANORAMICA
    'bg-emerald-500',   // VOLONTARI
    'bg-violet-500',    // SOCI E ASSOCIATI
    'bg-amber-500',     // GOVERNANCE
    'bg-rose-500',      // ATTIVITÀ
    'bg-cyan-500',      // DOCUMENTI
    'bg-indigo-500',    // REPORT E CONTABILITÀ
    'bg-purple-500',    // STRUMENTI AVANZATI
    'bg-slate-500',     // IMPOSTAZIONI
    'bg-red-500',       // AMMINISTRAZIONE
  ];
  
  const barColor = categoryColors[categoryIndex % categoryColors.length];
  
  return (
    <div className={`mb-1 relative transition-all duration-200 ${
      isOpen ? 'bg-slate-50/80' : ''
    }`}>
      {/* Barra laterale colorata per categoria aperta */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-r transition-all duration-200 ${
        isOpen ? barColor : 'bg-transparent'
      }`} />
      
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
          hasActiveItem 
            ? 'text-[#0055BB] bg-[#0055BB]/10' 
            : isOpen 
              ? 'text-slate-700 bg-slate-100/50' 
              : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
        }`}
        data-testid={`category-${category.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className="flex items-center gap-2">
          <CategoryIcon className={`w-4 h-4 ${isOpen ? 'text-slate-700' : ''}`} />
          <span>{category}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 transition-transform" />
        ) : (
          <ChevronRight className="w-4 h-4 transition-transform" />
        )}
      </button>
      
      <div className={`overflow-hidden transition-all duration-200 ease-in-out ${
        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="py-1 pl-2">
          {items.map((item) => {
            const Icon = item.icon;
            
            // If item is locked, show clickable state with lock icon that opens upgrade modal
            if (item.locked) {
              return (
                <button
                  key={item.path}
                  onClick={() => onLockedItemClick(item.requiredPlan, item.label)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2 mx-2 rounded-lg text-sm text-slate-400 hover:text-slate-500 hover:bg-slate-50 transition-colors cursor-pointer"
                  title={`Richiede piano ${getPlanDisplayName(item.requiredPlan)} - Clicca per scoprire di più`}
                  data-testid={`locked-${item.path.replace('/', '')}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </div>
                  <div className="flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded-full">
                    <Lock className="w-3 h-3" />
                    <span className="text-xs font-medium uppercase">{getPlanDisplayName(item.requiredPlan)}</span>
                  </div>
                </button>
              );
            }
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2 mx-2 rounded-lg text-sm transition-all ${
                    isActive 
                      ? 'bg-[#0055BB] text-white font-medium shadow-sm' 
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`
                }
                onClick={onItemClick}
                data-testid={`nav-${item.path.replace('/', '')}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  
  // State for upgrade modal
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeModalData, setUpgradeModalData] = useState({ requiredPlan: '', featureName: '' });
  
  // Handler for clicking on locked menu items
  const handleLockedItemClick = (requiredPlan, featureName) => {
    setUpgradeModalData({ requiredPlan, featureName });
    setUpgradeModalOpen(true);
  };
  
  // Keyboard shortcut for search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Get user plan (default to 'free')
  const userPlan = user?.plan || 'free';
  
  // Get menu structure based on user plan
  const menuStructure = getMenuStructure(userPlan);
  
  // Initialize open categories based on current path
  const getInitialOpenCategories = () => {
    const openCats = {};
    menuStructure.forEach(cat => {
      const hasActiveItem = cat.items.some(item => location.pathname === item.path && !item.locked);
      openCats[cat.category] = hasActiveItem;
    });
    // Always open PANORAMICA by default if no other category is active
    if (!Object.values(openCats).some(v => v)) {
      openCats['PANORAMICA'] = true;
    }
    return openCats;
  };
  
  const [openCategories, setOpenCategories] = useState(getInitialOpenCategories);

  const toggleCategory = (category) => {
    setOpenCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile menu button */}
      <button
        className="fixed top-4 left-4 z-50 md:hidden p-2 bg-white rounded-lg shadow-md"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
        aria-label="Menu"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="sidebar">
        <div className="p-5 border-b border-slate-100">
          <h1 className="text-xl font-bold text-[#0055BB]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Assietica Gest
          </h1>
          <p className="text-xs text-slate-500 italic">by Assietica</p>
          <p className="text-xs text-slate-400 mt-1">Il gestionale semplice e sicuro per la tua associazione</p>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {menuStructure.map((menuCat, index) => (
            <MenuCategory
              key={menuCat.category}
              category={menuCat.category}
              icon={menuCat.icon}
              items={menuCat.items}
              isOpen={openCategories[menuCat.category]}
              onToggle={() => toggleCategory(menuCat.category)}
              onItemClick={() => setSidebarOpen(false)}
              onLockedItemClick={handleLockedItemClick}
              currentPath={location.pathname}
              categoryIndex={index}
            />
          ))}
          
          {/* Admin menu - only visible for platform_admin */}
          {user?.role === 'platform_admin' && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <MenuCategory
                category={adminMenu.category}
                icon={adminMenu.icon}
                items={adminMenu.items}
                isOpen={openCategories[adminMenu.category] || location.pathname.startsWith('/admin')}
                onToggle={() => toggleCategory(adminMenu.category)}
                onItemClick={() => setSidebarOpen(false)}
                onLockedItemClick={handleLockedItemClick}
                currentPath={location.pathname}
                categoryIndex={9}
              />
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-[#0055BB] text-white">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="main-content">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="md:hidden w-10" /> {/* Spacer for mobile menu button */}
            
            {/* Search Button */}
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              data-testid="global-search-btn"
            >
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">Cerca...</span>
              <kbd className="hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-white border rounded">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>

            <div className="flex-1" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-[#0055BB] text-white text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/profilo')} data-testid="menu-profile">
                  <UserCircle className="w-4 h-4 mr-2" />
                  Profilo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/impostazioni')} data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" />
                  Impostazioni
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 md:p-8 max-w-7xl mx-auto">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="px-6 py-6 text-center border-t border-slate-100 bg-slate-50/50">
          <div className="max-w-4xl mx-auto space-y-2">
            <p className="text-sm font-medium text-slate-700">
              Assietica Gest <span className="font-normal italic">by Assietica</span>
            </p>
            <p className="text-xs text-slate-600 mt-1">
              Il gestionale semplice e sicuro per la tua associazione.
            </p>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed max-w-2xl mx-auto">
              Una piattaforma sviluppata e proprietaria di Assietica Società Cooperativa – Società Benefit, 
              progettata per supportare ETS, APS, ODV, Associazioni e Fondazioni nella gestione dei volontari, 
              delle attività e degli adempimenti amministrativi, organizzativi e burocratici.
            </p>
            <p className="text-xs text-slate-500 mt-3">
              © 2026 Assietica Società Cooperativa – Società Benefit
            </p>
            <p className="text-xs text-slate-500">
              Tutti i diritti riservati.
            </p>
            <div className="flex items-center justify-center gap-4 mt-3 text-xs">
              <NavLink to="/termini" className="text-slate-500 hover:text-[#0055BB] transition-colors">
                Termini di Utilizzo
              </NavLink>
              <span className="text-slate-300">|</span>
              <NavLink to="/condizioni-abbonamento" className="text-slate-500 hover:text-[#0055BB] transition-colors">
                Condizioni Abbonamento
              </NavLink>
              <span className="text-slate-300">|</span>
              <NavLink to="/privacy" className="text-slate-500 hover:text-[#0055BB] transition-colors">
                Privacy
              </NavLink>
              <span className="text-slate-300">|</span>
              <NavLink to="/cookie-policy" className="text-slate-500 hover:text-[#0055BB] transition-colors">
                Cookie
              </NavLink>
              <span className="text-slate-300">|</span>
              <NavLink to="/uso-intelligenza-artificiale" className="text-slate-500 hover:text-[#0055BB] transition-colors">
                Uso dell&apos;AI
              </NavLink>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Ideazione e sviluppo: Mauro Antonio Mura
            </p>
            <p className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-200">
              Verificare sempre con il proprio consulente legale/amministrativo la conformità delle procedure adottate dall'ente.
            </p>
          </div>
        </footer>
      </div>
      
      {/* Chatbot Widget */}
      <ChatbotWidget />
      
      {/* Global Search Dialog */}
      <RicercaGlobale open={searchOpen} onOpenChange={setSearchOpen} />
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        requiredPlan={upgradeModalData.requiredPlan}
        featureName={upgradeModalData.featureName}
        currentPlan={userPlan}
      />
    </div>
  );
}
